/**
 * Soporte de `?store=<id>` para links compartidos.
 *
 * Por que la tienda NO esta en el path
 * ------------------------------------
 * La eleccion de tienda es una preferencia del operador, no una coordenada de
 * la pantalla: cambiarla no deberia cambiar en que seccion estas. Si viviera en
 * el path (`/tienda/2/facturacion`), cada seccion tendria que reescribir la URL
 * al cambiar de tienda, y el selector -- que vive en el layout -- pasaria a
 * depender del router. La eleccion vive en `StoreAccessProvider` (memoria +
 * localStorage) y sobrevive a la navegacion sin que ninguna pantalla haga nada.
 *
 * Que aporta entonces el query param
 * ----------------------------------
 * Un solo caso, pero real: mandarle a alguien un link "mira ESTO en la tienda
 * 2". El param se aplica **una vez** y se borra de la URL con `replace`, para
 * que no quede una URL diciendo `store=1` mientras el selector muestra otra
 * cosa. Una URL que miente es peor que una URL sin informacion.
 *
 * Y si la tienda pedida no esta concedida, esto devuelve `denied` para que el
 * layout muestre un cartel explicito. Aplicar el fallback en silencio -- que es
 * lo que hace el provider con un id guardado que ya no vale -- estaria mal aca:
 * quien abre un link ajeno tiene que enterarse de que no tiene ese acceso, no
 * mirar los datos de otra tienda creyendo que son los del link.
 */

import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

import { useStoreAccess } from "../stores/StoreAccessProvider";

/** Nombre del query param. Publico porque es parte del contrato de los links. */
export const STORE_PARAM = "store";

export type StoreDeepLink =
  /** No hay param pendiente: o nunca hubo, o ya se aplico y se limpio. */
  | { readonly kind: "none" }
  /** El param no es un id de tienda (`?store=hola`). */
  | { readonly kind: "invalid"; readonly raw: string }
  /** Es un id valido, pero el servidor no confirmo esa tienda para el usuario. */
  | { readonly kind: "denied"; readonly raw: string };

export function useStoreDeepLink(): StoreDeepLink {
  const [params, setParams] = useSearchParams();
  const { state, selectStore } = useStoreAccess();
  const raw = params.get(STORE_PARAM);

  // Recuerda el ultimo valor aplicado para no re-aplicarlo (StrictMode monta
  // los efectos dos veces en desarrollo) ni pelearse con el selector si el
  // operador cambia de tienda a mano despues.
  const appliedRef = useRef<string | null>(null);

  const resolved: StoreDeepLink = (() => {
    if (raw === null) return { kind: "none" };
    // Mientras la tenencia se resuelve no hay veredicto posible: el layout ya
    // esta mostrando su propio "cargando".
    if (state.kind !== "ready") return { kind: "none" };
    const id = Number.parseInt(raw, 10);
    if (Number.isNaN(id) || String(id) !== raw.trim()) return { kind: "invalid", raw };
    const match = state.data.some((store) => store.id === id);
    return match ? { kind: "none" } : { kind: "denied", raw };
  })();

  useEffect(() => {
    // Sin param: ademas de no hacer nada, se olvida lo aplicado. Asi el mismo
    // link vuelve a funcionar si el operador cambio de tienda a mano y despues
    // lo reabre; el guard es contra el doble efecto de StrictMode, no una
    // memoria permanente.
    if (raw === null) {
      appliedRef.current = null;
      return;
    }
    if (state.kind !== "ready") return;
    if (resolved.kind !== "none") return;
    if (appliedRef.current === raw) return;

    const id = Number.parseInt(raw, 10);
    appliedRef.current = raw;
    selectStore(id);

    const next = new URLSearchParams(params);
    next.delete(STORE_PARAM);
    // `replace`: el link compartido no tiene que dejar una entrada extra en el
    // historial que devuelva al mismo lugar con el param puesto.
    setParams(next, { replace: true });
  }, [raw, resolved.kind, state, selectStore, params, setParams]);

  return resolved;
}
